import React, { Component } from 'react';
import styled, { css, ThemeProvider } from 'styled-components';
import {
  Card,
  CardContent,
  CardSpacing,
  Column,
  Container,
  Row
} from './CommonUiComponents';
import { Button } from './CommonUiComponents';
import {
  LoginErrorText,
  LoginFormTitle,
  LoginInputField,
  LoginInputLabel,
  StyledLoginForm,
  LoginLink,
  LoginButtonWrapper
} from '../style-guide/Login';
import StatusWrapper from './StatusWrapper';
import StatusLoader from './StatusLoader';
import { textGray } from '../utils/colors';
import apiRequest from '../utils/apiRequest';
import NoBook from './NoBook';

const LoginContainer = Container.extend`
  margin: 0;
  margin-left: auto;
`;

const TopSection = styled.div`
  margin: 0 auto;
  position: relative;
  margin-top: 4rem;

  @media all and (max-width: 1024px) {
    margin-top: 3rem;
    text-align: center;
  }
`;

const LoginCard = styled(Card)`
  background-color: #ffffff;
`;

const BookTitle = styled.h1`
  font-size: 4rem;
  line-height: 2.8125rem;
  color: ${textGray};
  margin-top: 0.75rem;

  ${props =>
    props.inverted &&
    css`
      color: #ffffff;
    `};

  @media all and (max-width: 40rem) {
    font-size: 2rem;
  }
`;

const SuccessTitle = styled.h2`
  font-size: 2rem;
  line-height: 2.8125rem;
  color: ${textGray};
  margin-top: 0.75rem;

  ${props =>
    props.inverted &&
    css`
      color: #ffffff;
    `};
`;

const BookCategory = styled.span`
  font-size: 0.75rem;
  text-transform: uppercase;
  color: ${textGray};
  font-weight: 400;
  letter-spacing: 1px;

  ${props =>
    props.inverted &&
    css`
      color: #ffffff;
    `};
`;

export default class Login extends Component {
  state = {
    bookData: null,
    fetchingBookData: false,
    licenseKey: '',
    email: '',
    errorMessage: null,
    registerSuccess: false,
    registering: false
  };

  componentDidMount() {
    this.fetchBookData();
    this.setBodyThemeColor();
  }

  componentDidUpdate() {
    this.setBodyThemeColor();
  }

  setBodyThemeColor() {
    const body = document.body;
    const { bookData } = this.state;

    if (bookData && bookData.theme) {
      body.style.backgroundColor = bookData.theme.primary;
    }
  }

  handleInputValue = e => {
    const { name, value } = e.target;

    this.setState({
      [name]: value
    });
  };

  async fetchBookData() {
    const {
      match: { params },
      setInvertedTheme,
      unsetInvertedTheme
    } = this.props;
    this.setState({ fetchingBookData: true });

    const response = await apiRequest(
      'wordpress',
      `bookdata/v1/login?id=${params.bookId}`
    );

    if (response.theme.text_color === 'dark') {
      setInvertedTheme();
    } else {
      unsetInvertedTheme();
    }

    this.setState({
      fetchingBookData: false,
      bookData: response
    });
  }

  handleKeyRegistration = async e => {
    e.preventDefault();
    const { licenseKey, email } = this.state;

    this.setState({
      registering: true
    });

    const res = await apiRequest('wordpress', 'userdata/v1/key', 'POST', {
      user_email: email,
      registerkey: licenseKey
    });

    this.setState({
      registering: false
    });

    switch (res.code) {
      case 'used_registerkey_given':
        this.setState({
          errorMessage: 'Lisensnøkkelen er allerede registrert.'
        });

        return false;
      case 'incorrect_registerkey_given':
        this.setState({
          errorMessage: 'Lisensnøkkel er ikke gyldig.'
        });

        return false;
      case 'id_mismatch':
        this.setState({
          errorMessage: 'Brukeren eksisterer ikke'
        });

        return false;
      case 'new_key_registered':
        this.setState({
          errorMessage: null,
          registerSuccess: true
        });

        return false;
      default:
        break;
    }
  };

  render() {
    const { invertedTheme } = this.props;
    const {
      bookData,
      fetchingBookData,
      errorMessage,
      registering,
      registerSuccess
    } = this.state;

    if (!bookData) return null;

    if (bookData.status === 404) {
      return (
        <NoBook inverted={invertedTheme}>
          Ojda, hva gjør du på denne URL'en?
        </NoBook>
      );
    }

    return (
      <ThemeProvider theme={bookData.theme}>
        <StatusLoader fullsize large busy={fetchingBookData}>
          <LoginContainer>
            <Row>
              <Column>
                <TopSection>
                  <BookCategory inverted={invertedTheme}>
                    {bookData.course_name}
                  </BookCategory>
                  <BookTitle inverted={invertedTheme}>
                    «{bookData.title}»
                  </BookTitle>
                </TopSection>
              </Column>
            </Row>
            <Row>
              <Column>
                <LoginCard>
                  <CardSpacing>
                    <CardContent>
                      <StatusWrapper busy={registering}>
                        {registerSuccess ? (
                          <SuccessTitle inverted={invertedTheme}>
                            Lisensnøkkel registrert! Du kan nå{' '}
                            <LoginLink noGutterTop to="logg-inn">
                              logge inn
                            </LoginLink>
                          </SuccessTitle>
                        ) : (
                          <StyledLoginForm
                            onSubmit={this.handleKeyRegistration}
                            method="POST"
                          >
                            <LoginFormTitle inverted={invertedTheme}>
                              Få tilgang til denne boken ved å registrere en ny
                              lisensnøkkel!
                            </LoginFormTitle>
                            <LoginInputLabel
                              htmlFor={'email'}
                              inverted={invertedTheme}
                            >
                              E-postadresse
                            </LoginInputLabel>
                            <LoginInputField
                              type="email"
                              name="email"
                              id="email"
                              onChange={this.handleInputValue}
                              inverted={invertedTheme}
                              disabled={registering}
                              required
                            />
                            <LoginInputLabel
                              htmlFor={'licensekey'}
                              inverted={invertedTheme}
                            >
                              Lisensnøkkel
                            </LoginInputLabel>
                            <LoginInputField
                              type="text"
                              name="licenseKey"
                              id="licensekey"
                              onChange={this.handleInputValue}
                              inverted={invertedTheme}
                              disabled={registering}
                              required
                            />
                            {errorMessage && (
                              <LoginErrorText>{errorMessage}</LoginErrorText>
                            )}
                            <LoginButtonWrapper>
                              <Button type="submit" disabled={registering}>
                                Registrer nøkkel
                              </Button>
                              <LoginLink to="logg-inn" disabled={registering}>
                                Tilbake til Logg inn
                              </LoginLink>
                            </LoginButtonWrapper>
                          </StyledLoginForm>
                        )}
                      </StatusWrapper>
                    </CardContent>
                  </CardSpacing>
                </LoginCard>
              </Column>
            </Row>
          </LoginContainer>
        </StatusLoader>
      </ThemeProvider>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/RegisterKey.js